// Front/src/components/Navbar.js
 
import { useState } from 'react';
import { Link, useNavigate, NavLink, useLocation } from 'react-router-dom'
import { useLogout } from '../hooks/useLogout'
import { useAuthContext } from '../hooks/useAuthContext'
import { useTranslate } from '../hooks/useTranslate';
import { useLanguageContext } from '../context/LanguageContext';
import '../style/NavBar.css';
import config from '../config';
import PropTypes from 'prop-types';
 
const Navbar = ({ darkMode, setDarkMode }) => {  // Receive darkMode and language from App.js
 
    const { logout } = useLogout()
    const { user } = useAuthContext()
    const location = useLocation();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const navigate = useNavigate(); // Define navigate
    const t = useTranslate();
    const { language, setLanguage } = useLanguageContext();

    // only role enums 3-6 can have access to searching by service call number.
    const storedUser = JSON.parse(localStorage.getItem("user"));
    const roles = storedUser?.roles || [];

    // for debugging
    const isAllowedSearch = roles.some(role => role >= 3 && role <= 6); // uncomment this to add the search service call feature
    //const isAllowedSearch = roles.some(role => role >= 3 && role <= 6) && false; // comment this to add the search service call feature

    // for debugging
    // console.log("storedUser = ", storedUser);
    // console.log("storedUser?.roles = ", storedUser?.roles);
 
    const handleClick = () => {
        logout();
        navigate('/'); // Redirect to login page after logout
    }
 
    const toggleSidebar = () => {
        setIsSidebarOpen((prevState) => !prevState);
    };
 
    // Dark Mode Toggle
    const toggleDarkMode = () => {
        setDarkMode(prevMode => !prevMode);
    };
 
    // Language Toggle
    const toggleLanguage = () => {
        setLanguage((prevLang) => (prevLang === 'en' ? 'he' : 'en'));
    };
 
    // Choose the correct logo based on dark mode
    const logoSrc = darkMode ? "/dark_shekel_logo_transparent.png" : "/shekel_logo_transparent.png";
 
    // Helper function to safely get translation string
    const getTranslation = (key) => {
        const value = t[key];
        if (typeof value === 'object') {
            return language === 'en' ? value.EnglishString : value.HebrewString;
        }
    
        return value;
    };    
 
    return (
        <header>
            <div className="container">
                {user && location.pathname !== '/login' && (
                    <div className="burger-menu" onClick={toggleSidebar}>
                        <div className={`line ${isSidebarOpen ? 'open' : ''}`}></div>
                        <div className={`line ${isSidebarOpen ? 'open' : ''}`}></div>
                        <div className={`line ${isSidebarOpen ? 'open' : ''}`}></div>
                        {isSidebarOpen && (
                            <div className="sidebar">
                                <ul>
                                    <li>
                                        <Link to="/mytasks">{getTranslation('myTasks')}</Link>
                                    </li>
                                    {isAllowedSearch && (
                                        <li>
                                        <Link to="/searchservicecall">{getTranslation('searchServiceCall')}</Link>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
 
                {/* Logo */}
                <div className="logo-wrapper">
                    <img src={logoSrc} alt="Shekel Logo" className="logo" />
                    <div className="version-label">v{config.CURRENT_VERSION}</div>
                </div>
 
                {/* Website Title */}
                <NavLink className={({ isActive }) => (isActive ? "title active" : "title")} to="/">
                    <h1>{getTranslation('technet')}</h1>
                </NavLink>
 
                <nav>
                    {/* Logout Button on Left */}
                    {user && (
                        <div className="logout-container-left">
                            <button className="logout-button-icon" onClick={handleClick}>
                                <svg className="logout-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                                    <polyline points="16 17 21 12 16 7" />
                                    <line x1="21" y1="12" x2="9" y2="12" />
                                </svg>
                                {getTranslation('logout')}
                            </button>
                        </div>
                    )}
 
                    {/* Dark Mode Button */}
                    <button onClick={toggleDarkMode} className="dark-mode-toggle">
                        <span className="dark-mode-text">
                            {darkMode ? `🌙 ${getTranslation('dark')}` : `☀️ ${getTranslation('light')}`}
                        </span>
                        <span className="dark-mode-icon">{darkMode ? "🌙" : "☀️"}</span>
                    </button>
                   
                    {/* Language Toggle Button - Text Only */}
                    <div className="language-toggle-wrapper">
                        <label className="switch">
                            <input
                            type="checkbox"
                            onChange={toggleLanguage}
                            checked={language === 'he'}
                            />
                            <span className="slider">
                            {language === 'en' ? 'English' : 'עברית'}
                            </span>
                        </label>
                    </div>
 
                    {/* User Block info */}
                    {user && (
                        <div className="nav-buttons">
                            <div className="user-info-block">
                                <div className="username-container">
                                    <span>{ user.UserName }</span>
                                </div>
                            </div>
                        </div>
                    )}
                </nav>
            </div>
        </header>
    );
};

Navbar.propTypes = {
    darkMode: PropTypes.bool.isRequired,
    setDarkMode: PropTypes.func.isRequired
};

export default Navbar
 
